import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Drawer,
  Button,
  Input,
  Checkbox,
  Select,
  InputNumber,
  Space,
  message,
  Divider,
} from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useSystemConfig } from "../../Hook/useSystemConfig";
import { renderCourierParcel, type PageSizeType } from "../templates";
import { openCashDrawer } from "../../lib/openCashDrawer";
import { usePrintConfigQuery } from "../../Services";
import { DRAWER_FORM_WIDTH, drawerTitle } from "../AppUi";

const { TextArea } = Input;

const PAGE_SIZE_OPTIONS = [
  { value: "A4", label: "A4 Paper" },
  { value: "80mm", label: "80mm Thermal" },
  { value: "A3", label: "A3 Paper" },
] satisfies Array<{ value: PageSizeType; label: string }>;

const SERVICE_OPTIONS = [
  { value: "Standard", label: "Standard" },
  { value: "Express", label: "Express" },
  { value: "Same Day", label: "Same Day" },
  { value: "Overnight", label: "Overnight" },
];

const TIME_OPTIONS = [
  { value: "Morning (8AM-12PM)", label: "Morning (8AM-12PM)" },
  { value: "Afternoon (12PM-5PM)", label: "Afternoon (12PM-5PM)" },
  { value: "Evening (5PM-8PM)", label: "Evening (5PM-8PM)" },
  { value: "Anytime", label: "Anytime" },
];

function generateTrackingCode(orderNo: string): string {
  const suffix = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `TRK-${orderNo}-${suffix}`;
}

interface CourierParcelDrawerProps {
  open: boolean;
  order: {
    order_no: string;
    customer_name?: string;
    customer_phone?: string;
    customer_address?: string;
    customer_email?: string;
    paid_amount?: number;
    due_amount?: number;
    courier_name?: string | null;
    courier_phone?: string | null;
    courier_assignment?: {
      courier_name?: string;
      courier_phone?: string;
      contact_person?: string;
      tracking_no?: string;
      status?: string;
    } | null;
    items?: Array<{
      product_name?: string;
      item_name?: string;
      name?: string;
      quantity: number;
      sku_code?: string;
      unit?: string;
      batch_no?: string;
    }>;
  } | null;
  onClose: () => void;
}

export const CourierParcelDrawer: React.FC<CourierParcelDrawerProps> = ({
  open,
  order,
  onClose,
}) => {
  const {
    storeName,
    address,
    phone,
    email,
    whatsapp,
    receiptFooter,
    get,
    refresh: refreshSystemConfig,
  } = useSystemConfig();
  const { data: printConfig } = usePrintConfigQuery(undefined, {
    enabled: open,
  });
  const posPrinterServer =
    printConfig?.pos_printer_server || "http://localhost:9100";

  const prevOpenRef = useRef(open);
  const trackingGenerated = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      refreshSystemConfig();
    }
    prevOpenRef.current = open;
  }, [open, refreshSystemConfig]);

  const [description, setDescription] = useState("");

  const [isFragile, setIsFragile] = useState(true);
  const [thisSideUp, setThisSideUp] = useState(false);
  const [keepDry, setKeepDry] = useState(false);
  const [doNotStack, setDoNotStack] = useState(false);
  const [perishable, setPerishable] = useState(false);
  const [hazardous, setHazardous] = useState(false);
  const [customHandling, setCustomHandling] = useState("");

  const [showCustomer, setShowCustomer] = useState(true);
  const [altDeliveryAddress, setAltDeliveryAddress] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [preferredDeliveryTime, setPreferredDeliveryTime] = useState<string>("Anytime");
  const [deliveryZone, setDeliveryZone] = useState("");
  const [returnAddress, setReturnAddress] = useState("");
  const [returnAddressNote, setReturnAddressNote] = useState("");
  const [trackingCode, setTrackingCode] = useState("");
  const [serviceType, setServiceType] = useState<string>("Standard");
  const [codAmount, setCodAmount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<PageSizeType>("A4");
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (open && order && !trackingGenerated.current) {
      setTrackingCode(
        order.courier_assignment?.tracking_no ||
          generateTrackingCode(order.order_no),
      );
      if (order.due_amount && order.due_amount > 0) {
        setCodAmount(order.due_amount);
      }
      trackingGenerated.current = true;
    }
    if (!open) {
      trackingGenerated.current = false;
    }
  }, [open, order]);

  const courierName =
    order?.courier_assignment?.courier_name || order?.courier_name || "";
  const courierPhone =
    order?.courier_assignment?.courier_phone || order?.courier_phone || "";

  const getHtml = useCallback(() => {
    if (!order) return "";
    const dateObj = new Date();
    return renderCourierParcel({
      storeName,
      address,
      phone,
      email,
      whatsapp,
      website: (get("website") as string) || "",
      logoUrl: (get("app_logo") as string) || "",
      footer: receiptFooter,
      orderNo: order.order_no,
      dateStr: dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      customerName: order.customer_name || "",
      customerPhone: order.customer_phone || "",
      customerAddress: order.customer_address || "",
      customerEmail: order.customer_email || "",
      description,
      isFragile,
      showCustomer,
      items: order.items || [],
      totalItems: (order.items || []).length,
      returnAddress,
      altDeliveryAddress,
      deliveryInstructions,
      preferredDeliveryTime,
      thisSideUp,
      keepDry,
      doNotStack,
      perishable,
      hazardous,
      customHandling,
      codAmount,
      paidAmount: order.paid_amount || 0,
      deliveryZone,
      returnAddressNote,
      trackingCode,
      serviceType,
      courierName,
      courierPhone,
      paidAmountFormatted: "",
      codAmountFormatted: "",
      pageSize,
      isA4: pageSize === "A4" || pageSize === "A3",
    });
  }, [
    order, storeName, address, phone, email, whatsapp, receiptFooter, get,
    description, isFragile, showCustomer,
    returnAddress, altDeliveryAddress, deliveryInstructions, preferredDeliveryTime,
    thisSideUp, keepDry, doNotStack, perishable, hazardous, customHandling,
    codAmount, deliveryZone, returnAddressNote, trackingCode, serviceType,
    courierName, courierPhone, pageSize,
  ]);

  const handlePrint = useCallback(async () => {
    if (!order) return;
    setIsPrinting(true);

    try {
      await openCashDrawer(posPrinterServer);
    } catch {
      message.warning("Could not open cash drawer. Proceeding to print.");
    }

    await new Promise((resolve) => setTimeout(resolve, 300));

    const frame = document.createElement("iframe");
    frame.style.cssText = "position:absolute;top:-9999px;left:-9999px;";
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document || frame.contentDocument;
    if (!doc) {
      message.error("Failed to create print document.");
      setIsPrinting(false);
      return;
    }

    doc.open();
    doc.write(getHtml());
    doc.close();

    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => {
        if (frame.parentNode) {
          frame.parentNode.removeChild(frame);
        }
        setIsPrinting(false);
      }, 1000);
    }, 500);
  }, [order, getHtml, posPrinterServer]);

  if (!order) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={drawerTitle("Courier Parcel Label")}
      width={560}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={isPrinting}
          >
            Open Drawer & Print
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {/* Page Size Selector */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Page Size
          </span>
          <Select
            size="small"
            value={pageSize}
            onChange={(v) => setPageSize(v)}
            style={{ width: 160 }}
            options={PAGE_SIZE_OPTIONS}
          />
        </div>

        <Divider className="my-1" />

        {/* Section: Return & Tracking */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Return &amp; Tracking
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Checkbox
              checked={!!returnAddress}
              onChange={(e) => {
                if (!e.target.checked) {
                  setReturnAddress("");
                } else {
                  setReturnAddress(
                    [storeName, address, `Tel: ${phone}`].filter(Boolean).join("\n"),
                  );
                }
              }}
            >
              Add return address label
            </Checkbox>
          </div>
          {returnAddress && (
            <TextArea
              rows={2}
              value={returnAddress}
              onChange={(e) => setReturnAddress(e.target.value)}
              className="mb-2"
            />
          )}
          <div className="flex gap-2 mb-2">
            <Input
              size="small"
              placeholder="Return address note (footer)"
              value={returnAddressNote}
              onChange={(e) => setReturnAddressNote(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
          <Input
            size="small"
            placeholder="Tracking code (auto-generated)"
            value={trackingCode}
            onChange={(e) => setTrackingCode(e.target.value)}
            addonAfter="QR on label"
          />
          {courierName && (
            <div className="mt-2 text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded px-2 py-1.5">
              <span className="font-semibold text-blue-700">
                Courier: {courierName}
              </span>
              {courierPhone && (
                <span className="text-gray-500"> | {courierPhone}</span>
              )}
            </div>
          )}
        </div>

        <Divider className="my-2" />

        {/* Section: Delivery Details */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Delivery Details
          </div>
          <Checkbox
            checked={showCustomer}
            onChange={(e) => setShowCustomer(e.target.checked)}
            className="mb-2"
          >
            Show customer details on label
          </Checkbox>
          <div className="flex gap-2 mb-2">
            <Select
              size="small"
              placeholder="Delivery time"
              value={preferredDeliveryTime}
              onChange={setPreferredDeliveryTime}
              style={{ flex: 1 }}
              options={TIME_OPTIONS}
            />
            <Input
              size="small"
              placeholder="Zone / Route"
              value={deliveryZone}
              onChange={(e) => setDeliveryZone(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
          <Input
            size="small"
            placeholder="Delivery instructions (e.g. Leave at door, Call before delivery)"
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            className="mb-2 w-full"
          />
          <TextArea
            rows={2}
            placeholder="Alternative delivery address (if different from customer address)"
            value={altDeliveryAddress}
            onChange={(e) => setAltDeliveryAddress(e.target.value)}
          />
        </div>

        <Divider className="my-2" />

        {/* Section: Handling Labels */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Handling Labels
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Checkbox checked={isFragile} onChange={(e) => setIsFragile(e.target.checked)}>
              <span className="text-red-600 font-semibold">Fragile</span>
            </Checkbox>
            <Checkbox checked={thisSideUp} onChange={(e) => setThisSideUp(e.target.checked)}>
              <span className="text-blue-600 font-semibold">This Side Up</span>
            </Checkbox>
            <Checkbox checked={keepDry} onChange={(e) => setKeepDry(e.target.checked)}>
              <span className="text-green-600 font-semibold">Keep Dry</span>
            </Checkbox>
            <Checkbox checked={doNotStack} onChange={(e) => setDoNotStack(e.target.checked)}>
              <span className="text-orange-600 font-semibold">Do Not Stack</span>
            </Checkbox>
            <Checkbox checked={perishable} onChange={(e) => setPerishable(e.target.checked)}>
              <span className="text-pink-600 font-semibold">Perishable / Temp Control</span>
            </Checkbox>
            <Checkbox checked={hazardous} onChange={(e) => setHazardous(e.target.checked)}>
              <span className="text-red-700 font-semibold">Hazardous Materials</span>
            </Checkbox>
          </div>
          <Input
            size="small"
            placeholder="Custom handling label text"
            value={customHandling}
            onChange={(e) => setCustomHandling(e.target.value)}
            className="mt-2 w-full"
          />
        </div>

        <Divider className="my-2" />

        {/* Section: Service & Payment */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Service &amp; Payment
          </div>
          <div className="flex gap-2 mb-2">
            <Select
              size="small"
              value={serviceType}
              onChange={setServiceType}
              style={{ flex: 1 }}
              options={SERVICE_OPTIONS}
            />
            <InputNumber
              size="small"
              min={0}
              precision={2}
              value={codAmount}
              onChange={(v) => setCodAmount(v || 0)}
              addonBefore="COD"
              style={{ flex: 1 }}
            />
          </div>
          <div className="text-xs text-gray-400">
            Order total: LKR {(order.paid_amount || 0).toFixed(2)} paid
            {order.due_amount > 0 ? ` | Due: LKR ${order.due_amount.toFixed(2)}` : ""}
          </div>
        </div>

        <Divider className="my-2" />

        {/* Section: Contents */}
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Contents / Description
          </div>
          <TextArea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Electronics, Documents, Clothing..."
          />
        </div>

        {/* Preview */}
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 mt-2">
          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-3 py-1.5 bg-gray-100 border-b border-gray-200">
            Preview
          </div>
          <div
            className="p-3"
            dangerouslySetInnerHTML={{ __html: getHtml() }}
            style={{
              transform: pageSize === "80mm" ? "scale(0.7)" : "scale(0.42)",
              transformOrigin: "top left",
              width: pageSize === "80mm" ? "72mm" : "210mm",
            }}
          />
        </div>
      </div>
    </Drawer>
  );
};
